import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useAriaRangeAttributes } from '../../util/aria/useAriaRangeAttributes.js';
import { useTimer } from '../../util/timer.js';
import { Box } from '../Box/Box.js';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly.js';
import { backgroundColorToken } from './IndicatorBar.mappers.js';

var _excluded = ["size", "maxValue", "value", "intent", "label"];
var INITIAL_DELAY_MS = 800;
var calculatePercentage = function calculatePercentage(maxValue, value) {
  var boundedValue = Math.min(Math.max(0, value), maxValue);
  var boundedMaxValue = Math.max(0, maxValue);
  return boundedValue / boundedMaxValue * 100;
};

/**
 * An indicator bar is like a friendly guide, leading you through tasks like booking a hotel or rating a stay. With each step, it lights the way, turning a complex process into a simple and enjoyable journey. Think of it as drawing a line on a map, directly to your perfect vacation destination.
 */
var IndicatorBar = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var size = _ref.size,
    maxValue = _ref.maxValue,
    value = _ref.value,
    intent = _ref.intent,
    label = _ref.label,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    percentage = _React$useState2[0],
    setPercentage = _React$useState2[1];
  var isFirstRender = React.useRef(true);
  useTimer({
    delay: INITIAL_DELAY_MS,
    callback: function callback() {
      var newPercentage = calculatePercentage(maxValue, value);
      isFirstRender.current = false;
      setPercentage(newPercentage);
    }
  });
  React.useEffect(function () {
    if (isFirstRender.current) return;
    var newPercentage = calculatePercentage(maxValue, value);
    setPercentage(newPercentage);
  }, [value, maxValue]);
  var ariaRangeAttributes = useAriaRangeAttributes({
    maxValue: maxValue,
    minValue: 0,
    value: value
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, _extends({
    ref: ref,
    backgroundColor: "deprecated.neutral.4",
    width: "100%",
    height: size === 's' ? '4px' : '8px',
    borderRadius: "base",
    overflow: "hidden",
    role: "progressbar",
    "aria-label": label
  }, ariaRangeAttributes, rest), /*#__PURE__*/React.createElement(Box, {
    width: "".concat(percentage, "%"),
    backgroundColor: backgroundColorToken(intent),
    height: "100%",
    borderRadius: "base",
    role: "presentation",
    transition: "width 0.3s ease-out"
  })), /*#__PURE__*/React.createElement(ScreenReaderOnly, {
    role: "status"
  }, label));
});
IndicatorBar.defaultProps = {
  size: 'm',
  intent: 'primary'
};
var IndicatorBarMemoized = /*#__PURE__*/React.memo(IndicatorBar);
IndicatorBarMemoized.displayName = 'IndicatorBar';

export { IndicatorBarMemoized as IndicatorBar };
//# sourceMappingURL=IndicatorBar.js.map
